import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { stagger } from '../utils/quinn';

const pillar1Def: SlotGroupDefinition = {
  label: 'Pillar 1',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Prime Neighborhoods' },
    description: { label: 'Description', type: 'text', defaultValue: 'Good schools, parks and everyday errands a few minutes from the driveway.' },
  },
};

const pillar2Def: SlotGroupDefinition = {
  label: 'Pillar 2',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Thoughtful Design' },
    description: { label: 'Description', type: 'text', defaultValue: 'Three- and four-bedroom homes with garages, yards and room to spread out.' },
  },
};

const pillar3Def: SlotGroupDefinition = {
  label: 'Pillar 3',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Community-focused Amenities' },
    description: { label: 'Description', type: 'text', defaultValue: 'Pools, playgrounds and green space you share with neighbors, not strangers.' },
  },
};

const pillar4Def: SlotGroupDefinition = {
  label: 'Pillar 4',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Commitment to Service' },
    description: { label: 'Description', type: 'text', defaultValue: 'On-site teams handle the lawn, the repairs and the follow-through.' },
  },
};

const pillar5Def: SlotGroupDefinition = {
  label: 'Pillar 5',
  fields: {
    name: { label: 'Name', type: 'text', defaultValue: 'Environmental Responsibility' },
    description: { label: 'Description', type: 'text', defaultValue: 'Energy-efficient homes built to last and to cost less to run.' },
  },
};

const pillarItem =
  'flex items-baseline gap-6 border-b border-b-[color-mix(in_srgb,var(--q-on-primary)_22%,transparent)] px-0 py-5 max-[767px]:flex-wrap max-[767px]:gap-x-3.5 max-[767px]:gap-y-1 max-[767px]:py-4';
const pillarNumber = 'w-10 shrink-0 font-heading text-[20px] text-[--q-accent-on-primary] max-[767px]:w-7 max-[767px]:text-[18px]';
const pillarCopy =
  'flex min-w-0 flex-auto items-baseline gap-6 max-[767px]:flex-wrap max-[767px]:gap-x-3.5 max-[767px]:gap-y-1 min-[1200px]:max-[1439px]:flex-col min-[1200px]:max-[1439px]:items-start min-[1200px]:max-[1439px]:gap-1.5';
const pillarName =
  'w-60 shrink-0 text-[18px] font-medium leading-6 max-[767px]:w-auto max-[767px]:text-[16px] max-[767px]:leading-[22px] min-[1200px]:max-[1439px]:w-auto';
const pillarDesc = 'text-[15px] leading-6 opacity-[.85] max-[767px]:w-full max-[767px]:text-[14px] max-[767px]:leading-[21px]';

/** Line icons for the pillar cards: pin, house, people, wrench. */
function PillarIcon({ index }: { index: number }) {
  return (
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[--q-surface]" aria-hidden="true">
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        stroke="var(--q-primary-on-paper)"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {index === 0 && (
          <>
            <path d="M10 17.5s5.5-4.9 5.5-9.5a5.5 5.5 0 0 0-11 0c0 4.6 5.5 9.5 5.5 9.5z" />
            <circle cx="10" cy="8" r="2" />
          </>
        )}
        {index === 1 && (
          <>
            <path d="M3 9.5 10 3.5l7 6" />
            <path d="M5 8.2V16.5h10V8.2" />
            <path d="M8.5 16.5v-4.5h3v4.5" />
          </>
        )}
        {index === 2 && (
          <>
            <circle cx="7.5" cy="7" r="2.5" />
            <circle cx="13.5" cy="8" r="2" />
            <path d="M2.5 16.5c0-2.8 2.2-5 5-5s5 2.2 5 5" />
            <path d="M12.5 12.3c2 .2 3.5 1.9 3.5 4" />
          </>
        )}
        {index === 3 && <path d="M13.6 3.3a3.8 3.8 0 0 0-4.4 5.1L3 14.6V17h2.4l6.2-6.2a3.8 3.8 0 0 0 5.1-4.4l-2.3 2.3-2.1-.5-.5-2.1z" />}
      </svg>
    </span>
  );
}

const card =
  'flex flex-col gap-6 rounded-lg border border-[--q-border] bg-[--q-paper] p-7 max-[767px]:flex-row max-[767px]:items-start max-[767px]:gap-5 max-[767px]:p-6';
const cardCopy = 'flex min-w-0 flex-col gap-2 max-[767px]:gap-1.5';
const cardName = 'm-0 font-heading text-[24px] font-normal leading-[30px] max-[767px]:text-[22px] max-[767px]:leading-7';
const cardDesc = 'm-0 text-[15px] leading-6 text-[--q-muted] max-[767px]:leading-[22px]';

interface WhyQuinnProps {
  /** "band": the five numbered pillars on the home page; "page": four pillar cards on the Why Quinn page. */
  variant?: 'band' | 'page';
}

export function WhyQuinn({ variant = 'band' }: WhyQuinnProps) {
  const p1 = useSlotGroup('whyquinn.pillar_1', pillar1Def);
  const p2 = useSlotGroup('whyquinn.pillar_2', pillar2Def);
  const p3 = useSlotGroup('whyquinn.pillar_3', pillar3Def);
  const p4 = useSlotGroup('whyquinn.pillar_4', pillar4Def);
  const p5 = useSlotGroup('whyquinn.pillar_5', pillar5Def);

  if (variant === 'page') {
    return (
      <section className="section [&.section]:pb-[104px] [&.section]:pt-24 max-[767px]:[&.section]:pb-[72px] max-[767px]:[&.section]:pt-16" id="why-quinn">
        <div className="section__head [&.section\_\_head]:mb-14 max-[767px]:[&.section\_\_head]:mb-8" data-reveal>
          <div className="section__lead-block">
            <RichTextContent path="whyquinn.page_eyebrow" as="div" className="eyebrow" />
            <RichTextContent path="whyquinn.page_title" as="h2" className="h2" />
          </div>
          <RichTextContent path="whyquinn.page_lead" as="p" className="section__lead text-lg leading-7 max-w-[600px]" />
        </div>
        <div className="grid grid-cols-4 gap-6 max-[1199px]:grid-cols-2 max-[1199px]:gap-5 max-[767px]:grid-cols-1 max-[767px]:gap-4">
          <div className={card} data-reveal style={stagger(0)}>
            <PillarIcon index={0} />
            <EditableSlotGroup groupPath="whyquinn.pillar_1" definition={pillar1Def} className={cardCopy}>
              <h3 className={cardName}>{p1.name}</h3>
              <p className={cardDesc}>{p1.description}</p>
            </EditableSlotGroup>
          </div>
          <div className={card} data-reveal style={stagger(1)}>
            <PillarIcon index={1} />
            <EditableSlotGroup groupPath="whyquinn.pillar_2" definition={pillar2Def} className={cardCopy}>
              <h3 className={cardName}>{p2.name}</h3>
              <p className={cardDesc}>{p2.description}</p>
            </EditableSlotGroup>
          </div>
          <div className={card} data-reveal style={stagger(2)}>
            <PillarIcon index={2} />
            <EditableSlotGroup groupPath="whyquinn.pillar_3" definition={pillar3Def} className={cardCopy}>
              <h3 className={cardName}>{p3.name}</h3>
              <p className={cardDesc}>{p3.description}</p>
            </EditableSlotGroup>
          </div>
          <div className={card} data-reveal style={stagger(3)}>
            <PillarIcon index={3} />
            <EditableSlotGroup groupPath="whyquinn.pillar_4" definition={pillar4Def} className={cardCopy}>
              <h3 className={cardName}>{p4.name}</h3>
              <p className={cardDesc}>{p4.description}</p>
            </EditableSlotGroup>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="section flex items-start justify-between gap-24 overflow-hidden !bg-[--q-primary] !py-28 text-[--q-on-primary] [&_a:focus-visible]:outline-[--q-accent-on-primary] max-[1199px]:flex-col max-[1199px]:gap-12 max-[1199px]:!py-24 max-[767px]:gap-7 max-[767px]:!py-16"
      id="why-quinn"
    >
      <div className="relative flex w-[560px] shrink-0 flex-col gap-6 max-[1199px]:w-auto" data-reveal>
        <div className="flex items-center gap-3">
          <RichTextContent path="whyquinn.eyebrow" as="span" className="eyebrow eyebrow--accent" />
        </div>
        <RichTextContent path="whyquinn.title" as="h2" className="h1 [&_p]:m-0" />
        <RichTextContent
          path="whyquinn.lead"
          as="p"
          className="m-0 text-lg leading-7 opacity-[.86] max-[767px]:text-[15px] max-[767px]:leading-6"
        />
      </div>
      <ol className="relative m-0 list-none grow border-t border-t-[color-mix(in_srgb,var(--q-on-primary)_22%,transparent)] p-0">
        <li className={pillarItem} data-reveal style={stagger(0)}>
          <span className={pillarNumber}>01</span>
          <EditableSlotGroup groupPath="whyquinn.pillar_1" definition={pillar1Def} className={pillarCopy}>
            <span className={pillarName}>{p1.name}</span>
            <span className={pillarDesc}>{p1.description}</span>
          </EditableSlotGroup>
        </li>
        <li className={pillarItem} data-reveal style={stagger(1)}>
          <span className={pillarNumber}>02</span>
          <EditableSlotGroup groupPath="whyquinn.pillar_2" definition={pillar2Def} className={pillarCopy}>
            <span className={pillarName}>{p2.name}</span>
            <span className={pillarDesc}>{p2.description}</span>
          </EditableSlotGroup>
        </li>
        <li className={pillarItem} data-reveal style={stagger(2)}>
          <span className={pillarNumber}>03</span>
          <EditableSlotGroup groupPath="whyquinn.pillar_3" definition={pillar3Def} className={pillarCopy}>
            <span className={pillarName}>{p3.name}</span>
            <span className={pillarDesc}>{p3.description}</span>
          </EditableSlotGroup>
        </li>
        <li className={pillarItem} data-reveal style={stagger(3)}>
          <span className={pillarNumber}>04</span>
          <EditableSlotGroup groupPath="whyquinn.pillar_4" definition={pillar4Def} className={pillarCopy}>
            <span className={pillarName}>{p4.name}</span>
            <span className={pillarDesc}>{p4.description}</span>
          </EditableSlotGroup>
        </li>
        <li className={pillarItem} data-reveal style={stagger(4)}>
          <span className={pillarNumber}>05</span>
          <EditableSlotGroup groupPath="whyquinn.pillar_5" definition={pillar5Def} className={pillarCopy}>
            <span className={pillarName}>{p5.name}</span>
            <span className={pillarDesc}>{p5.description}</span>
          </EditableSlotGroup>
        </li>
      </ol>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'WhyQuinn',
  displayName: 'Why Quinn',
  tokens: {
    'whyquinn.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Why Quinn</p>' },
    'whyquinn.title': { label: 'Title', type: 'richtext', defaultValue: '<p>Homes with<br>Life in Mind</p>' },
    'whyquinn.lead': {
      label: 'Lead',
      type: 'richtext',
      defaultValue: '<p>Everything you love about owning a home. None of the upkeep. Rent a house, a yard and a neighborhood from Quinn, and leave the lawn, the repairs and the HVAC to us.</p>',
    },
    'whyquinn.page_eyebrow': {
      label: 'Page Eyebrow',
      description: 'Why Quinn page only.',
      type: 'richtext',
      defaultValue: '<p>What makes Quinn different</p>',
    },
    'whyquinn.page_title': {
      label: 'Page Title',
      description: 'Why Quinn page only.',
      type: 'richtext',
      defaultValue: '<p>Four promises, kept at every Quinn community.</p>',
    },
    'whyquinn.page_lead': {
      label: 'Page Lead',
      description: 'Why Quinn page only.',
      type: 'richtext',
      defaultValue: '<p>The same four promises at every Quinn neighborhood, and what they mean at <span data-type="field-token" data-field="property_name"></span>.</p>',
    },
  },
  slotGroups: {
    'whyquinn.pillar_1': pillar1Def,
    'whyquinn.pillar_2': pillar2Def,
    'whyquinn.pillar_3': pillar3Def,
    'whyquinn.pillar_4': pillar4Def,
    'whyquinn.pillar_5': pillar5Def,
  },
};

(WhyQuinn as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default WhyQuinn;
